import type { ReportTypeId } from '../../config/reportTypes';
import type { PathToGoAnalysisSnapshot } from '../pathToGo/reportPlan';
import type { SiteEntity } from '../../types/site';
import {
  analysisRevisionReferenceV1Schema,
  decisionAnalysisSnapshotV1Schema,
  type AnalysisReadinessState,
  type AnalysisRevisionReferenceV1,
  type DecisionAnalysisDomain,
  type DecisionAnalysisInputEnvelopeV1,
} from '../../types/decisionAnalysis';
import type { ReportOverallStatus, SiteRagReport } from '../../types/siteRag';
import { verifyDecisionAnalysisSnapshotIdentity } from './identity';

/**
 * Browser-loaded current analysis. Operational report identifiers are only
 * compatibility links; immutable analysis identity remains the authority.
 */
export interface BrowserDecisionAnalysisSnapshot extends PathToGoAnalysisSnapshot {
  reportId?: string;
  reportRevisionId?: string;
  /** Non-ready refresh result retained beside the last passing current revision. */
  isLatestAttempt?: boolean;
}

export interface BrowserDecisionAnalysisOperation {
  refreshState?:
    | 'queued'
    | 'refreshing'
    | 'ready'
    | 'stale'
    | 'held'
    | 'degraded'
    | 'failed';
  refreshRequestedAt?: string;
  refreshStartedAt?: string;
  refreshUpdatedAt?: string;
  refreshCompletedAt?: string;
  refreshFailedAt?: string;
  refreshReason?: string;
  refreshError?: string;
}

function validIsoOrFallback(
  candidates: readonly (string | undefined)[],
  fallback: string,
): string {
  return candidates.find(value => (
    typeof value === 'string' && Number.isFinite(Date.parse(value))
  )) ?? fallback;
}

/**
 * Overlay the server-owned refresh lifecycle onto the last passing pointer.
 * Its immutable identity remains unchanged, but positive decisions fail closed
 * while a replacement is queued/running/failed. The previous content remains
 * viewable, exactly as the web-first refresh UX requires.
 */
export function revisionWithOperationalRefresh(
  revision: AnalysisRevisionReferenceV1,
  operation: BrowserDecisionAnalysisOperation,
): AnalysisRevisionReferenceV1 {
  const state = operation.refreshState;
  if (!state || state === 'ready') return revision;
  const readinessState: AnalysisReadinessState = state;
  const detail = operation.refreshReason
    ?? operation.refreshError
    ?? (
      state === 'queued'
        ? 'A replacement analysis is queued.'
        : state === 'refreshing'
          ? 'A replacement analysis is being prepared.'
          : state === 'stale'
            ? 'The analysis inputs changed and require refresh.'
            : `The latest analysis refresh is ${state}.`
    );
  const code = state === 'failed'
    ? 'quality-failed'
    : state === 'held'
      ? 'quality-held'
      : state === 'degraded'
        ? 'quality-degraded'
        : 'input-stale';
  const reason = { code, message: detail } as const;
  return {
    ...revision,
    readiness: {
      ...revision.readiness,
      state: readinessState,
      ready: false,
      canUseForDecision: false,
      current: false,
      qualityReady: ['failed', 'held', 'degraded'].includes(state)
        ? false
        : revision.readiness.qualityReady,
      reason,
      reasons: [reason],
      evaluatedAt: validIsoOrFallback([
        operation.refreshUpdatedAt,
        operation.refreshFailedAt,
        operation.refreshStartedAt,
        operation.refreshRequestedAt,
        operation.refreshCompletedAt,
      ], revision.readiness.evaluatedAt),
    },
  };
}

/**
 * Pointer refresh states whose LATEST ATTEMPT is the committed outcome at
 * rest, so its currency is judged on the attempt's own frozen input.
 *
 * 'failed' is deliberately absent: an evidence-driven retry is its recovery
 * route, so the reaper keeps re-enqueueing it on an evidence change. 'stale',
 * 'queued' and 'refreshing' name work that is not at rest.
 *
 * Both states are included (release-owner sub-choice, default as recorded): a
 * degraded attempt can come from a transient cause (for example a missing
 * reviewer verdict), and judging it on input alone stops the automatic retry
 * on evidence changes to sections it does not bind. A manual rerun still runs.
 */
export const LATEST_ATTEMPT_JUDGED_REFRESH_STATES: ReadonlySet<AnalysisReadinessState> =
  new Set<AnalysisReadinessState>(['held', 'degraded']);

/** The pointer fields that name a latest attempt, from either pointer shape. */
export interface LatestAttemptPointerFields {
  refreshState?: unknown;
  refreshInputHash?: unknown;
  latestAttemptReference?: unknown;
  /** analysisId of the pointer's own (last passing) reference, when it has one. */
  referenceAnalysisId?: unknown;
}

/**
 * The latest attempt a held/degraded pointer is judged on. This is the ONE
 * basis the invalidation reaper (`decisionAnalysisInvalidation`) and Path to GO
 * readiness (server `pathToGoDecisionAnalysisContext`, browser
 * `SiteLandingPage`) share, so the reaper's enqueue and the "Refresh required"
 * badge cannot drift apart.
 *
 * finalizeReportRevision commits a NON-ready attempt by writing only the
 * operational overlay: `latestAttemptReference` = the attempt, `refreshState`
 * = its readiness state, `refreshInputHash` = its inputHash. The pointer's own
 * reference stays the last PASSING revision (absent on a first run), which is
 * OLDER than the attempt. This returns the attempt only for exactly that shape:
 *  - `refreshState` is in LATEST_ATTEMPT_JUDGED_REFRESH_STATES;
 *  - `latestAttemptReference` parses, names this site and domain, is not
 *    ready, and its own readiness state IS the pointer's refreshState;
 *  - `refreshInputHash` equals the attempt's inputHash. The reaper's enqueue
 *    and the worker's failure mirror move refreshState / refreshInputHash
 *    (queued, refreshing, failed, stale) WITHOUT touching
 *    latestAttemptReference, so an older attempt can sit under a newer run;
 *    this guard refuses it;
 *  - it is not the pointer's own reference (a legacy shape finalize no longer
 *    writes; there is no separate attempt to judge).
 * `null` means "no attempt to judge": every caller keeps its previous path.
 * The attempt is never decision-usable (the pointer was not advanced to it).
 */
export function latestAttemptJudgedOnInput(
  pointer: LatestAttemptPointerFields,
  expected: { siteId: string; domain: DecisionAnalysisDomain },
  /**
   * The refresh states whose latest attempt is judged. Defaults to the shared
   * set above; only the automatic-rebind gate widens it (to a `stale` commit
   * at rest), never readiness or the reaper's up-to-date decision.
   */
  states: ReadonlySet<AnalysisReadinessState> = LATEST_ATTEMPT_JUDGED_REFRESH_STATES,
): AnalysisRevisionReferenceV1 | null {
  const refreshState = pointer.refreshState;
  if (
    typeof refreshState !== 'string'
    || !states.has(refreshState as AnalysisReadinessState)
  ) return null;
  const parsed = analysisRevisionReferenceV1Schema.safeParse(pointer.latestAttemptReference);
  if (!parsed.success) return null;
  const attempt = parsed.data;
  if (
    attempt.siteId !== expected.siteId
    || attempt.domain !== expected.domain
    || attempt.readiness.ready
    || attempt.readiness.canUseForDecision
    || attempt.readiness.state !== refreshState
    || pointer.refreshInputHash !== attempt.inputHash
    || pointer.referenceAnalysisId === attempt.analysisId
  ) return null;
  return attempt;
}

/**
 * Which revision the pointer's `reportId` belongs to. finalizeReportRevision
 * writes `reportId` (the committed report) on EVERY commit, ready or not,
 * together with `latestAttemptReference`; only a READY commit also replaces
 * the pointer's own reference. So the report id names the LATEST ATTEMPT's
 * report, and it is the reference's report only when the two are the same
 * revision (or no attempt is recorded).
 *
 * Attaching it to the reference of a held/degraded/stale pointer grafted the
 * older passing analysis onto the newer attempt's report: live 2026-09-30,
 * Goldenrod's Public Sentiment row showed the 30 Sept report (v2, SED v10)
 * dated 17 Sept, the date of the passing analysis the held attempt did not
 * replace.
 */
export function pointerReportIdOwnerAnalysisId(pointer: {
  reference?: { analysisId: string } | null;
  latestAttemptReference?: { analysisId: string } | null;
}): string | null {
  return pointer.latestAttemptReference?.analysisId ?? pointer.reference?.analysisId ?? null;
}

function referenceMatchesSnapshot(
  entry: PathToGoAnalysisSnapshot,
): boolean {
  const { revision, snapshot } = entry;
  return (
    verifyDecisionAnalysisSnapshotIdentity(snapshot).valid
    && revision.analysisId === snapshot.analysisId
    && revision.siteId === snapshot.siteId
    && revision.domain === snapshot.domain
    && revision.inputHash === snapshot.inputHash
    && revision.contentHash === snapshot.contentHash
    && revision.createdAt === snapshot.createdAt
    && revision.snapshotPath.trim().length > 0
  );
}

/**
 * Safe fallback for hosts where the current-analysis API is unavailable.
 * Invalid or partially embedded compatibility payloads are excluded.
 */
export function analysisSnapshotsFromReports(
  reports: readonly SiteRagReport[],
): PathToGoAnalysisSnapshot[] {
  return reports.flatMap((report) => {
    if (!report.decisionAnalysis || !report.analysisRevision) return [];
    const snapshot = decisionAnalysisSnapshotV1Schema.safeParse(
      report.decisionAnalysis,
    );
    const revision = analysisRevisionReferenceV1Schema.safeParse(
      report.analysisRevision,
    );
    if (!snapshot.success || !revision.success) return [];
    const entry: PathToGoAnalysisSnapshot = {
      revision: revision.data,
      snapshot: snapshot.data,
    };
    return referenceMatchesSnapshot(entry) ? [entry] : [];
  });
}

/**
 * Bind the server's live envelopes to verified immutable revisions. Comparing
 * a snapshot with its own frozen input says nothing about current source data.
 * Missing live authority therefore leaves the domain absent and fail-closed.
 */
export function currentAnalysisInputsFromSnapshots(
  entries: readonly PathToGoAnalysisSnapshot[],
  currentInputs: Partial<Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>> = {},
): Partial<Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>> {
  const output: Partial<
    Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>
  > = {};
  for (const entry of entries) {
    if (!referenceMatchesSnapshot(entry)) continue;
    const current = currentInputs[entry.snapshot.domain];
    if (current?.siteId !== entry.snapshot.siteId || current.domain !== entry.snapshot.domain) continue;
    output[entry.snapshot.domain] = current;
  }
  return output;
}

function statusForBand(
  band: BrowserDecisionAnalysisSnapshot['snapshot']['semantic']['systemDecision']['band'],
): ReportOverallStatus {
  if (band === 'go') return 'green';
  if (band === 'no-go') return 'red';
  // P1.4 — a withheld semantic decision projects as `withheld`, never amber.
  if (band === 'withheld') return 'withheld';
  return 'amber';
}

function syntheticReport(
  site: SiteEntity,
  entry: BrowserDecisionAnalysisSnapshot,
): SiteRagReport {
  const { snapshot, revision } = entry;
  return {
    id: !entry.isLatestAttempt && entry.reportId
      ? entry.reportId
      : `analysis-${snapshot.analysisId}`,
    reportType: snapshot.domain as ReportTypeId,
    siteId: site.id,
    siteName: site.name,
    address: site.address,
    location: {
      lat: site.location.lat,
      lng: site.location.lng,
      address: site.address,
    },
    // NaN deliberately means "not scored" to legacy display helpers. Unknown
    // semantic scores must never be projected as a misleading zero.
    overallScore: snapshot.semantic.score?.overall.value ?? Number.NaN,
    overallStatus: statusForBand(snapshot.semantic.systemDecision.band),
    summary: snapshot.semantic.summary,
    timeSeries: [],
    risks: [],
    recommendations: [],
    createdAt: snapshot.createdAt,
    decisionAnalysis: snapshot,
    analysisRevision: revision,
    analysisReadiness: revision.readiness,
  };
}

/**
 * Adapt exact semantic revisions into the legacy report-shaped collection
 * consumed by Findings. Existing report payloads are cloned and enriched when
 * a compatibility link exists; otherwise a minimal render-only projection is
 * appended. Neither path mutates or fabricates authoritative evidence.
 */
export function projectDecisionAnalysesIntoReports(
  site: SiteEntity,
  reports: readonly SiteRagReport[],
  entries: readonly BrowserDecisionAnalysisSnapshot[],
): SiteRagReport[] {
  const projected = [...reports];
  for (const entry of entries) {
    if (!referenceMatchesSnapshot(entry) || entry.snapshot.siteId !== site.id) {
      continue;
    }
    // Exact analysis identity first; the report-id link is only a
    // compatibility fallback, so a link can never graft this analysis onto a
    // report that another revision's identity already names.
    const byIdentity = projected.findIndex(report => (
      report.analysisRevision?.analysisId === entry.revision.analysisId
      || report.decisionAnalysis?.analysisId === entry.snapshot.analysisId
    ));
    const index = byIdentity >= 0
      ? byIdentity
      : projected.findIndex(report => (
        !entry.isLatestAttempt && entry.reportId != null && report.id === entry.reportId
      ));
    if (index < 0) {
      projected.push(syntheticReport(site, entry));
      continue;
    }
    const report = projected[index]!;
    projected[index] = {
      ...report,
      reportType: entry.snapshot.domain as ReportTypeId,
      siteId: site.id,
      updatedAt: entry.snapshot.createdAt,
      overallScore:
        entry.snapshot.semantic.score?.overall.value ?? Number.NaN,
      overallStatus: statusForBand(entry.snapshot.semantic.systemDecision.band),
      summary: entry.snapshot.semantic.summary,
      decisionAnalysis: entry.snapshot,
      analysisRevision: entry.revision,
      analysisReadiness: entry.revision.readiness,
    };
  }
  return projected;
}
